/* Okno — jedno okno pro celou appku (DESIGN-PRAVIDLA §5): ui/dialog na desktopu,
 * vaul drawer zespodu na mobilu (< 768 px). Zatemnění jen var(--scrim), Esc zavře,
 * fokus zůstane v okně a po zavření se vrátí na spouštěč (Radix / vaul).
 *   <Okno otevreno={o} zmenOtevreno={setO} nazev="Upravit vstupy" info="…"
 *         paticka={<Button onClick={ulozit}>Uložit</Button>}>
 *     …obsah…
 *   </Okno>
 * Props:
 *   otevreno, zmenOtevreno(bool)   řízený stav
 *   nazev       (povinné) titulek okna (Dialog.Title → aria-labelledby)
 *   info        text InfoTipu vedle titulku
 *   popis       popis pro čtečku (vizuálně skrytý; Dialog.Description)
 *   paticka     akce dole (1 primární)
 *   sirka       "sm" | "md" (výchozí) | "lg" — jen desktop
 *   vynutit     "dialog" | "drawer" — přebije detekci mobilu (testy, náhledy)
 *   testid      (výchozí "okno")
 */
import * as Dialog from "@radix-ui/react-dialog";
import { Drawer } from "vaul";
import { X } from "lucide-react";
import useIsMobile from "@/hooks/useIsMobile";
import InfoTip from "@/components/ui/InfoTip";
import { useTextZakladu } from "@/components/ui/zakladTexty";

const SIRKY = { sm: "max-w-sm", md: "max-w-lg", lg: "max-w-3xl" };

function Hlava({ Titulek, Popis, nazev, info, popis, tz, Zavrit, testid }) {
  return (
    <div className="flex items-center gap-2 border-b border-[var(--line)] px-4 py-3 md:px-5">
      <div className="flex min-w-0 flex-1 items-center gap-1">
        <Titulek className="min-w-0 truncate text-base font-semibold text-[var(--ink)]">{nazev}</Titulek>
        {info ? <span className="inline-flex items-center px-1"><InfoTip nazev={nazev} text={info} testid={`${testid}-info`} /></span> : null}
      </div>
      {popis ? <Popis className="sr-only">{popis}</Popis> : <Popis className="sr-only">{nazev}</Popis>}
      <Zavrit asChild>
        <button
          type="button"
          aria-label={tz("zavrit")}
          data-testid={`${testid}-zavrit`}
          className="-mr-2 inline-flex h-11 w-11 items-center justify-center rounded-full text-[var(--muted)] transition-colors duration-150 hover:bg-[var(--accent-soft)] hover:text-[var(--ink)]"
        >
          <X size={18} aria-hidden="true" />
        </button>
      </Zavrit>
    </div>
  );
}

export default function Okno({ otevreno, zmenOtevreno, nazev, info, popis, paticka, sirka = "md", vynutit, testid = "okno", children }) {
  const tz = useTextZakladu();
  const mobil = useIsMobile(768);
  const drawer = vynutit ? vynutit === "drawer" : mobil;
  const zmen = (v) => { if (typeof zmenOtevreno === "function") zmenOtevreno(!!v); };
  const telo = <div className="overflow-y-auto px-4 py-4 text-sm text-[var(--ink)] md:px-5">{children}</div>;
  const pata = paticka ? (
    <div className="flex flex-wrap items-center justify-end gap-2 border-t border-[var(--line)] px-4 py-3 md:px-5">{paticka}</div>
  ) : null;

  if (drawer) {
    return (
      <Drawer.Root open={!!otevreno} onOpenChange={zmen} shouldScaleBackground={false}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 z-50 bg-[var(--scrim)]" />
          <Drawer.Content
            data-testid={testid}
            data-druh="drawer"
            className="fixed inset-x-0 bottom-0 z-50 flex max-h-[90dvh] flex-col rounded-t-[var(--radius)] border border-[var(--line)] bg-[var(--surface)] shadow-[var(--shadow-float)] outline-none"
          >
            <div className="mx-auto mt-2 h-1.5 w-10 shrink-0 rounded-full bg-[var(--line)]" aria-hidden="true" />
            <Hlava Titulek={Drawer.Title} Popis={Drawer.Description} Zavrit={Drawer.Close} nazev={nazev} info={info} popis={popis} tz={tz} testid={testid} />
            {telo}
            {pata}
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    );
  }

  return (
    <Dialog.Root open={!!otevreno} onOpenChange={zmen}>
      <Dialog.Portal>
        {/* Otevření i ZAVŘENÍ přes `t-overlay`/`t-modal` (index.css, transitions-dev):
            250 ms nájezd ze scale 0,96, 150 ms odjezd. Dřív okno jen naskočilo
            a zavíralo se skokem. */}
        <Dialog.Overlay className="fixed inset-0 z-50 bg-[var(--scrim)] t-overlay" />
        <Dialog.Content
          data-testid={testid}
          data-druh="dialog"
          className={`fixed left-1/2 top-1/2 z-50 flex max-h-[85vh] w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-[var(--radius)] border border-[var(--line)] bg-[var(--surface)] shadow-[var(--shadow-float)] outline-none t-modal ${SIRKY[sirka] || SIRKY.md}`}
        >
          <Hlava Titulek={Dialog.Title} Popis={Dialog.Description} Zavrit={Dialog.Close} nazev={nazev} info={info} popis={popis} tz={tz} testid={testid} />
          {telo}
          {pata}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
